import { createSlice, createSelector } from "@reduxjs/toolkit";
import { coreApi } from "api/core";
import { setShowMessage } from "redux/slices/uiSlice";
import moment from "moment";

const baseUrl = "/routes";

const initialState = {
  routes: [],
  completed: [],
  current: [],
  archived: [],
  route: null,
  loading: false,
  pagination: {
    current: { nextCursor: null, hasNextPage: false, totalCount: null, isLoadingMore: false },
    recent: { nextCursor: null, hasNextPage: false, totalCount: null, isLoadingMore: false },
    archived: { nextCursor: null, hasNextPage: false, totalCount: null, isLoadingMore: false }
  }
};

const routeSlice = createSlice({
  name: "routes",
  initialState,
  reducers: {
    setRoutes: (state, action) => {
      state.routes = action.payload;
    },
    setCurrent: (state, action) => {
      state.current = action.payload;
    },
    setCompleted: (state, action) => {
      state.completed = action.payload;
    },
    setArchived: (state, action) => {
      state.archived = action.payload;
    },
    appendCurrent: (state, action) => {
      state.current = [...state.current, ...action.payload];
    },
    appendCompleted: (state, action) => {
      state.completed = [...state.completed, ...action.payload];
    },
    appendArchived: (state, action) => {
      state.archived = [...state.archived, ...action.payload];
    },
    setRoute: (state, action) => {
      state.route = action.payload;
    },
    setRouteLoading: (state) => {
      state.loading = true;
    },
    setRouteReady: (state) => {
      state.loading = false;
    },
    setPagination: (state, action) => {
      const { routeType, paginationData } = action.payload;
      state.pagination[routeType] = { ...state.pagination[routeType], ...paginationData };
    },
    appendRoutes: (state, action) => {
      const { routeType, routes } = action.payload;
      if (state[routeType] && Array.isArray(state[routeType])) {
        state[routeType] = [...state[routeType], ...routes];
      } else {
        state[routeType] = [...routes];
      }
    },
    resetPagination: (state, action) => {
      const { routeType } = action.payload;
      state.pagination[routeType] = { nextCursor: null, hasNextPage: false, totalCount: null, isLoadingMore: false };
    },
  },
});

export const {
  setRoutes,
  setRouteLoading,
  setRouteReady,
  setCompleted,
  setCurrent,
  setArchived,
  setRoute,
  setPagination,
  appendRoutes,
  appendCurrent,
  appendCompleted,
  appendArchived,
  resetPagination
} = routeSlice.actions;
export default routeSlice.reducer;

export const getRoute =
  (id, allDriverLocations = false) =>
    async (dispatch) => {
      const url = baseUrl + `/${id}${allDriverLocations ? "?allDriverLocations=1" : ""}`;
      // dispatch(setRouteLoading());
      try {
        const res = await coreApi.fetch(url);
        dispatch(setRoute(res));

        return res;
      } catch (err) {
        console.error(err);
      } finally {
        // dispatch(setRouteReady());
      }
    };

export const getRoutesForCurrentTours = () => async (dispatch) => {
  dispatch(setRouteLoading());
  try {
    const currentDate = moment().utc().startOf("day").format("YYYY-MM-DD");
    const currentRoutes = await coreApi.fetch(`${baseUrl}?ended=0`);
    const routesForCurrentDate = await coreApi.fetch(`${baseUrl}?start_date_from=${currentDate}`);
    const allRoutes = [...currentRoutes, ...routesForCurrentDate];
    let updatedRoutes = allRoutes.map((data) => ({
      ...data,
      is_favourite: false,
    }));

    dispatch(setRoutes(updatedRoutes));
  } catch (err) {
    console.error(err);
  } finally {
    dispatch(setRouteReady());
  }
};

export const getRoutes = () => async (dispatch) => {
  dispatch(setRouteLoading());
  try {
    const routes = await coreApi.fetch(baseUrl);
    let update = routes.map((data) => {
      return {
        ...data,
        is_favourite: false,
      };
    });
    dispatch(setRoutes(update));
  } catch (err) {
    console.error(err);
  } finally {
    dispatch(setRouteReady());
  }
};
export const getCurrentRoutes = (options = {}) => async (dispatch, getState) => {
  if (options.skipIfCached && getState().routes.current.length > 0) {
    return;
  }
  dispatch(setRouteLoading());
  try {
    const routes = await coreApi.fetch(`${baseUrl}?ended=0`);
    let favourites = localStorage.getItem("current-tours-favourites");
    favourites = favourites ? favourites.split(",") : [];
    favourites = favourites.map(Number);

    const newData = routes
      .map((data) => {
        return {
          ...data,
          is_favourite: favourites.includes(data.id),
          progress: "In Progress",
        };
      })
      .sort((a, b) => b.is_favourite - a.is_favourite);

    dispatch(setCurrent(newData));
  } catch (err) {
    console.error(err);
  } finally {
    dispatch(setRouteReady());
  }
};

export const skipStop = (routeId, customerId, index, reason, all = false) => async (dispatch) => {
  dispatch(setRouteLoading());

  try {
    const queryParam = all ? `?all=${all}` : '';
    await coreApi.put(`${baseUrl}/skip-stop/${routeId}/${customerId}${queryParam}`, { reason, index: customerId ? null : index });
    await dispatch(loadFirstPageRoutes('current', 20, 'id'));
  } catch (err) {
    console.error(err);
  } finally {
    dispatch(setRouteReady());
  }
};

export const getFinisedRoutes = (options = {}) => async (dispatch, getState) => {
  if (options.skipIfCached && getState().routes.completed.length > 0) {
    return;
  }
  dispatch(setRouteLoading());
  try {
    const routes = await coreApi.fetch(
      `${baseUrl}?started=1&ended=1&end_date_from=${moment()
        .utc()
        .subtract(5, "days")
        .startOf("day")
        .format("YYYY-MM-DD")}`
    );

    const newData = routes.map((data) => {
      return {
        ...data,
        is_favourite: false,
        progress: "Completed",
      };
    });
    dispatch(setCompleted(newData));
  } catch (err) {
    console.error(err);
  } finally {
    dispatch(setRouteReady());
  }
};

export const getArchivedRoutes = (options = {}) => async (dispatch, getState) => {
  if (options.skipIfCached && getState().routes.archived.length > 0) {
    return;
  }
  dispatch(setRouteLoading());
  try {
    const routes = await coreApi.fetch(
      `${baseUrl}?started=1&ended=1&end_date_to=${moment()
        .utc()
        .subtract(5, "days")
        .startOf("day")
        .format("YYYY-MM-DD")}`
    );
    let update = routes.map((data) => {
      return {
        ...data,
      };
    });
    let newData = update.map((data) => {
      return {
        ...data,
        is_favourite: false,
        progress: "Archived",
      };
    });

    dispatch(setArchived(newData));
  } catch (err) {
    console.error(err);
  } finally {
    dispatch(setRouteReady());
  }
};
export const createRoute = (payload, t) => async (dispatch) => {
  dispatch(setRouteLoading());

  try {
    const res = await coreApi.post(baseUrl, payload);
    const routes = Array.isArray(res) ? res : res ? [res] : [];
    const warnings = routes.flatMap(
      (route) => route?.time_constraint_warnings || route?.route_planning_metadata?.time_constraint_warnings || []
    );
    if (warnings.length) {
      const summary = warnings
        .map((v) => {
          const name = v.customer_name || v.customer_number || v.order_number || v.customer_id;
          return `${name}: ${v.window_from || "?"}–${v.window_to || "?"}`;
        })
        .join("; ");
      dispatch(
        setShowMessage({
          description: t
            ? `${t("Time constraint warnings")}: ${summary}`
            : `Time constraint warnings: ${summary}`,
          type: "warning",
        })
      );
    }
    return { data: res, hasTimeConstraintWarnings: warnings.length > 0 };
  } catch (err) {
    console.error(err);
    const errorKey =
      err?.response?.data?.error || err?.error || err?.message || "ROUTE_CREATE_FAILED";
    const violations = err?.response?.data?.details?.violations;
    let description = t ? t(errorKey) : errorKey;
    if (errorKey === "TIME_CONSTRAINT_VIOLATED" && Array.isArray(violations) && violations.length) {
      const summary = violations
        .map((v) => {
          const name = v.customer_name || v.customer_number || v.order_number || v.customer_id;
          return `${name}: ${v.window_from || "?"}–${v.window_to || "?"}`;
        })
        .join("; ");
      description = t
        ? `${t("TIME_CONSTRAINT_VIOLATED")}: ${summary}`
        : `TIME_CONSTRAINT_VIOLATED: ${summary}`;
    } else if (errorKey === "DELIVERY_TILL_BEFORE_START_TIME" && t) {
      description = t("DELIVERY_TILL_BEFORE_START_TIME");
    }
    dispatch(
      setShowMessage({
        description,
        type: "error",
      })
    );
    throw err;
  } finally {
    dispatch(setRouteReady());
  }
};

export const undoRoute = (id, t) => async (dispatch) => {
  try {
    await coreApi.delete(`${baseUrl}/undo/${id}`);
    dispatch(
      setShowMessage({
        description: t("The route is undone"),
        type: "success",
      })
    );
  } catch (error) {
    dispatch(
      setShowMessage({
        description: t("Route undo failed"),
        type: "error",
      })
    );
  } finally {
    dispatch(setRouteReady());
  }
};

export const updateRoutePositions = (routeId, pathway, t) => async (dispatch) => {
  dispatch(setRouteLoading());
  try {
    const response = await coreApi.put(`${baseUrl}/${routeId}/positions`, { pathway });
    const msg = response?.message ? t(response.message) : t("Route positions updated successfully");
    dispatch(
      setShowMessage({
        description: msg,
        type: "success",
      })
    );
    return response;
  } catch (err) {
    const errorKey = err?.response?.data?.error;
    const errorMsg = errorKey ? t(errorKey) : err.message ? t(err.message) : t("Failed to update route positions");
    dispatch(
      setShowMessage({
        description: errorMsg,
        type: "error",
      })
    );
  } finally {
    dispatch(setRouteReady());
  }
};

export const getRouteById = (id) => async (dispatch) => {
  dispatch(setRouteLoading());
  try {
    const route = await coreApi.fetch(`/routes/${id}`);
    dispatch(setRoute(route));
    return route;
  } catch (err) {
    console.error(err);
  } finally {
    dispatch(setRouteReady());
  }
};

export const getDriverLocations = (routeId) => async (dispatch) => {
  try {
    const driverLocations = await coreApi.fetch(`${baseUrl}/${routeId}/driver-locations`);
    return driverLocations;
  } catch (err) {
    console.error(err);
    return [];
  }
};

export const loadFirstPageRoutes = (routeType, limit = 20, sortBy = 'id', filters = {}) => async (dispatch) => {
  dispatch(setRouteLoading());
  try {
    const params = new URLSearchParams({
      limit: limit.toString(),
      sort_by: sortBy,
      ...filters
    });

    let endpoint = baseUrl;
    if (routeType === 'current') {
      endpoint += '?ended=0';
    } else if (routeType === 'recent') {
      endpoint += `?started=1&ended=1&end_date_from=${moment().utc().subtract(5, "days").startOf("day").format("YYYY-MM-DD")}`;
    } else if (routeType === 'archived') {
      endpoint += `?started=1&ended=1&end_date_to=${moment().utc().subtract(5, "days").startOf("day").format("YYYY-MM-DD")}`;
    }

    const fullUrl = `${endpoint}&${params}`;
    const response = await coreApi.axiosInstance.get(fullUrl);
    const routes = Array.isArray(response.data) ? response.data : [];

    const nextCursor = response.headers?.['x-next-cursor'] || response.headers?.get?.('x-next-cursor') || null;
    const totalCount = response.headers?.['x-total-count'] ? parseInt(response.headers['x-total-count']) : null;

    // If no cursor but we have total count and got fewer routes than limit, there might be more data
    const hasMoreData = !nextCursor && totalCount && routes.length === limit;

    let processedRoutes = routes;
    if (routeType === 'current') {
      let favourites = localStorage.getItem("current-tours-favourites");
      favourites = favourites ? favourites.split(",") : [];
      favourites = favourites.map(Number);

      processedRoutes = routes
        .map((data) => ({
          ...data,
          is_favourite: favourites.includes(data.id),
          progress: "In Progress",
        }))
        .sort((a, b) => b.is_favourite - a.is_favourite);
    } else if (routeType === 'recent') {
      processedRoutes = routes
        .map((data) => ({
          ...data,
          is_favourite: false,
          progress: "Completed",
        }))
        .sort((a, b) => (new Date(b.end_date) || 0) - (new Date(a.end_date) || 0));
    } else if (routeType === 'archived') {
      processedRoutes = routes.map((data) => ({
        ...data,
        is_favourite: false,
        progress: "Archived",
      }));
    }

    dispatch(setPagination({
      routeType,
      paginationData: {
        nextCursor,
        hasNextPage: !!nextCursor || hasMoreData,
        totalCount,
        isLoadingMore: false
      }
    }));

    if (routeType === 'current') {
      dispatch(setCurrent(processedRoutes));
    } else if (routeType === 'recent') {
      dispatch(setCompleted(processedRoutes));
    } else if (routeType === 'archived') {
      dispatch(setArchived(processedRoutes));
    }
  } catch (err) {
    console.error(err);
  } finally {
    dispatch(setRouteReady());
  }
};

export const loadNextPageRoutes = (routeType, limit = 20, filterParams = {}) => async (dispatch, getState) => {
  const state = getState();
  const pagination = state.routes.pagination[routeType];

  if (!pagination.hasNextPage || pagination.isLoadingMore) {
    return;
  }

  dispatch(setPagination({
    routeType,
    paginationData: { isLoadingMore: true }
  }));

  try {
    let params;
    let endpoint = baseUrl;

    if (pagination.nextCursor) {
      // Use cursor-based pagination
      params = new URLSearchParams({
        limit: limit.toString(),
        cursor: pagination.nextCursor
      });
    } else {
      // Fallback: use offset-based pagination when no cursor is available
      const currentRoutes = state.routes[routeType];
      const offset = currentRoutes.length;
      params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });
    }

    if (routeType === 'current') {
      endpoint += '?ended=0';
    } else if (routeType === 'recent') {
      endpoint += `?started=1&ended=1&end_date_from=${moment().utc().subtract(5, "days").startOf("day").format("YYYY-MM-DD")}`;
    } else if (routeType === 'archived') {
      endpoint += `?started=1&ended=1&end_date_to=${moment().utc().subtract(5, "days").startOf("day").format("YYYY-MM-DD")}`;
    }

    // Add filter parameters if provided
    if (Object.keys(filterParams).length > 0) {
      const filterQueryString = new URLSearchParams(filterParams).toString();
      endpoint += `&${filterQueryString}`;
    }

    const fullUrl = `${endpoint}&${params}`;

    const response = await coreApi.axiosInstance.get(fullUrl);
    const routes = Array.isArray(response.data) ? response.data : [];

    const nextCursor = response.headers?.['x-next-cursor'] || response.headers?.get?.('x-next-cursor') || null;

    let processedRoutes = routes;
    if (routeType === 'current') {
      let favourites = localStorage.getItem("current-tours-favourites");
      favourites = favourites ? favourites.split(",") : [];
      favourites = favourites.map(Number);

      processedRoutes = routes
        .map((data) => ({
          ...data,
          is_favourite: favourites.includes(data.id),
          progress: "In Progress",
        }))
        .sort((a, b) => b.is_favourite - a.is_favourite);
    } else if (routeType === 'recent') {
      processedRoutes = routes
        .map((data) => ({
          ...data,
          is_favourite: false,
          progress: "Completed",
        }))
        .sort((a, b) => (new Date(b.end_date) || 0) - (new Date(a.end_date) || 0));
    } else if (routeType === 'archived') {
      processedRoutes = routes.map((data) => ({
        ...data,
        is_favourite: false,
        progress: "Archived",
      }));
    }

    if (routeType === 'current') {
      dispatch(appendCurrent(processedRoutes));
    } else if (routeType === 'recent') {
      dispatch(appendCompleted(processedRoutes));
    } else if (routeType === 'archived') {
      dispatch(appendArchived(processedRoutes));
    }
    dispatch(setPagination({
      routeType,
      paginationData: {
        nextCursor,
        hasNextPage: !!nextCursor,
        isLoadingMore: false
      }
    }));
  } catch (err) {
    console.error(err);
    dispatch(setPagination({
      routeType,
      paginationData: { isLoadingMore: false }
    }));
  }
};

const routesSelector = ({ routes }) => routes.routes;
const routeSelector = ({ routes }) => routes.route;
const currentSelector = ({ routes }) => routes.current;
const completedSelector = ({ routes }) => routes.completed;
const archivedSelector = ({ routes }) => routes.archived;
const routeStatusSelector = ({ routes }) => routes.loading;
const paginationSelector = ({ routes }) => routes.pagination;

export const selectRoute = createSelector(routeSelector, (route) => route);
export const selectCurrent = createSelector(currentSelector, (current) => current);
export const selectCompleted = createSelector(completedSelector, (completed) => completed);
export const selectArchived = createSelector(archivedSelector, (archived) => archived);
export const selectRoutes = createSelector(routesSelector, (routes) => routes);
export const selectRouteStatus = createSelector(routeStatusSelector, (loading) => loading);
export const selectPagination = createSelector(paginationSelector, (pagination) => pagination);
export const selectCurrentPagination = createSelector(paginationSelector, (pagination) => pagination.current);
export const selectRecentPagination = createSelector(paginationSelector, (pagination) => pagination.recent);
export const selectArchivedPagination = createSelector(paginationSelector, (pagination) => pagination.archived);
